import type { WidgetPlacement, PlacementSlot } from './style-types';
import { PLACEMENT_SLOT_OPTIONS } from './style-types';
import { Monitor, Tablet, Smartphone } from 'lucide-react';

interface Props {
  placement: WidgetPlacement;
  onChange: (next: WidgetPlacement) => void;
}

const DEFAULT_VISIBILITY = { desktop: true, tablet: true, mobile: true };

export function PlacementEditor({ placement, onChange }: Props) {
  const visibility = { ...DEFAULT_VISIBILITY, ...(placement.visibility ?? {}) };
  const activeSlot: PlacementSlot = placement.slot ?? 'before-footer';

  const setSlot = (slot: PlacementSlot) => {
    onChange({ ...placement, slot });
  };

  const toggleVisibility = (breakpoint: keyof typeof DEFAULT_VISIBILITY) => {
    const next = { ...visibility, [breakpoint]: !visibility[breakpoint] };
    onChange({ ...placement, visibility: next });
  };

  const anyVisible = visibility.desktop || visibility.tablet || visibility.mobile;

  return (
    <div className="space-y-6">
      {/* Position */}
      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          Position auf der Seite
        </p>
        <div className="grid grid-cols-1 gap-2">
          {PLACEMENT_SLOT_OPTIONS.map(({ value, label, description }) => {
            const isActive = value === activeSlot;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSlot(value)}
                className={`flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors ${
                  isActive
                    ? 'border-brand-500 bg-brand-500/10'
                    : 'border-surface-300 bg-surface-100 hover:border-surface-400'
                }`}
              >
                <div
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                    isActive ? 'border-brand-400 bg-brand-500' : 'border-surface-400'
                  }`}
                >
                  {isActive && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-neutral-100">{label}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">{description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Visibility */}
      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          Sichtbarkeit nach Gerät
        </p>
        <div className="grid grid-cols-3 gap-2">
          <VisibilityToggle
            icon={<Monitor className="h-4 w-4" />}
            label="Desktop"
            active={visibility.desktop}
            onToggle={() => toggleVisibility('desktop')}
          />
          <VisibilityToggle
            icon={<Tablet className="h-4 w-4" />}
            label="Tablet"
            active={visibility.tablet}
            onToggle={() => toggleVisibility('tablet')}
          />
          <VisibilityToggle
            icon={<Smartphone className="h-4 w-4" />}
            label="Mobil"
            active={visibility.mobile}
            onToggle={() => toggleVisibility('mobile')}
          />
        </div>
        {!anyVisible && (
          <p className="mt-2 text-xs text-amber-400">
            Achtung: Das Widget ist auf keinem Gerät sichtbar und wird nirgends angezeigt.
          </p>
        )}
      </div>
    </div>
  );
}

function VisibilityToggle({
  icon,
  label,
  active,
  onToggle,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`flex flex-col items-center gap-1.5 rounded-lg border px-3 py-3 transition-colors ${
        active
          ? 'border-brand-500 bg-brand-500/10 text-neutral-100'
          : 'border-surface-300 bg-surface-100 text-neutral-500 hover:border-surface-400'
      }`}
      aria-pressed={active}
    >
      <span className={active ? 'text-brand-400' : 'text-neutral-500'}>{icon}</span>
      <span className="text-xs font-medium">{label}</span>
      <span className="text-[10px] text-neutral-500">{active ? 'Sichtbar' : 'Ausgeblendet'}</span>
    </button>
  );
}
